import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { authInstance, documentsInstance, schedulerInstance } from '@/http/axios/instances'
import { getBearerToken } from '@/services/localStorage'
import queryString from 'query-string'

export class Http {
  private axiosInstance: AxiosInstance

  private static getConfig = (config?: AxiosRequestConfig) => {
    const defaultConfig = {
      headers: {
        Authorization: getBearerToken(),
      },
    }

    if (!config) {
      return { ...defaultConfig }
    }

    const { headers, ...rest } = config
    return {
      ...rest,
      headers: {
        ...defaultConfig.headers,
        ...headers,
      } as any,
    }
  }

  constructor(instance: AxiosInstance) {
    this.axiosInstance = instance
  }

  private handleError = (error: any) => {
    if (error.code === 'ERR_CANCELED') {
      console.debug('Request canceled:', error.message)
    }
    else if (error.response) {
      const { data, status, statusText } = error.response
      console.debug('Request failed with status code:', status)
      throw data !== '' ? data : { message: `${status} ${statusText}`, status }
    }
    else {
      console.debug('Request failed:', error)
      throw error
    }
  }

  async get<T = any>(url: string, queryParams?: any, config?: AxiosRequestConfig) {
    try {
      if (queryParams) {
        const query = queryString.stringify(queryParams)
        url = url.includes('?') ? `${url}&${query}` : `${url}?${query}`
      }
      const res = await this.axiosInstance.get<T>(url, Http.getConfig(config))
      return res?.data
    }
    catch (error: any) {
      this.handleError(error)
    }
  }

  async post<T = any>(url: string, data: Record<string, any>, config?: AxiosRequestConfig) {
    try {
      const res = await this.axiosInstance.post<T>(url, data, Http.getConfig(config))
      return res?.data
    }
    catch (error: any) {
      this.handleError(error)
    }
  }

  async put<T = any>(url: string, data: Record<string, any>, config?: AxiosRequestConfig) {
    try {
      const res = await this.axiosInstance.put<T>(url, data, Http.getConfig(config))
      return res?.data
    }
    catch (error: any) {
      this.handleError(error)
    }
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig) {
    try {
      const res = await this.axiosInstance.delete<T>(url, Http.getConfig(config))
      return res?.data
    }
    catch (error: any) {
      this.handleError(error)
    }
  }
}

export const authHttp = new Http(authInstance)
export const schedulerHttp = new Http(schedulerInstance)
export const documentsHttp = new Http(documentsInstance)
